import { useRouter } from "expo-router";
import { useEffect, useRef } from "react";
import { StyleSheet, View, Animated, TouchableOpacity } from "react-native";
import { Text, Icon } from "react-native-paper";

import { colors } from "@/lib/theme";

interface ActivationScreenProps {
    onDismiss?: () => void;
}

export function ActivationScreen({ onDismiss }: ActivationScreenProps) {
    const router = useRouter();
    const scaleAnim = useRef(new Animated.Value(0)).current;
    const fadeAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        // Entrance animation
        Animated.sequence([
            Animated.spring(scaleAnim, {
                toValue: 1,
                friction: 4,
                tension: 60,
                useNativeDriver: true
            }),
            Animated.timing(fadeAnim, {
                toValue: 1,
                duration: 400,
                useNativeDriver: true
            })
        ]).start();

        // Auto-redirect after 5s
        const timer = setTimeout(() => {
            handleGoToVisits();
        }, 5000);

        return () => clearTimeout(timer);
    }, []);

    function handleGoToVisits() {
        onDismiss?.();
        router.replace("/(tabs)");
    }

    return (
        <View style={styles.container}>
            <View style={styles.content}>
                {/* Animated checkmark */}
                <Animated.View style={[styles.checkCircle, { transform: [{ scale: scaleAnim }] }]}>
                    <Icon source="check" size={64} color="#FFFFFF" />
                </Animated.View>

                {/* Text */}
                <Animated.View style={{ opacity: fadeAnim }}>
                    <Text style={styles.title}>Congratulations!</Text>
                    <Text style={styles.subtitle}>
                        Your onboarding is complete. You're ready to start receiving cases.
                    </Text>
                </Animated.View>

                {/* CTA */}
                <Animated.View style={[styles.buttonContainer, { opacity: fadeAnim }]}>
                    <TouchableOpacity style={styles.button} onPress={handleGoToVisits}>
                        <Text style={styles.buttonText}>Go to My Visits</Text>
                        <Icon source="arrow-right" size={18} color="#FFFFFF" />
                    </TouchableOpacity>
                </Animated.View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        ...StyleSheet.absoluteFill,
        backgroundColor: colors.background,
        zIndex: 100,
        justifyContent: "center",
        alignItems: "center"
    },
    content: {
        alignItems: "center",
        paddingHorizontal: 32,
        gap: 24
    },
    checkCircle: {
        width: 120,
        height: 120,
        borderRadius: 60,
        backgroundColor: colors.success,
        alignItems: "center",
        justifyContent: "center",
        shadowColor: colors.success,
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.4,
        shadowRadius: 20,
        elevation: 10
    },
    title: {
        fontSize: 28,
        fontWeight: "800",
        color: colors.textPrimary,
        textAlign: "center",
        marginBottom: 8
    },
    subtitle: {
        fontSize: 16,
        color: colors.textSecondary,
        textAlign: "center",
        lineHeight: 24
    },
    buttonContainer: {
        width: "100%",
        marginTop: 16
    },
    button: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        backgroundColor: colors.primary,
        borderRadius: 14,
        paddingVertical: 16
    },
    buttonText: {
        fontSize: 16,
        fontWeight: "700",
        color: "#FFFFFF"
    }
});
